import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ChevronLeft, Save, Clock } from "lucide-react";
import { CalendarBuilder } from "@/components/CalendarBuilder";
import { formatLongDate, formatTime } from "@/lib/dateUtils";

export default function EditEvent() {
  const { id } = useParams();
  const nav = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Common fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [location, setLocation] = useState("");

  // Calendar settings
  const [calSettings, setCalSettings] = useState({
    date_format: "dd/mm/yyyy",
    time_format: "12h",
    visibility: "public",
    field_email: "optional",
    field_phone: "optional",
  });

  // Potluck slot draft (for adding new one)
  const [newSlot, setNewSlot] = useState({ title: "", quantity: 1, when: "", slot_type: "item" });

  // Calendar add-slots state
  const [addDates, setAddDates] = useState([]);
  const [addSlots, setAddSlots] = useState([]);

  const isCalendar = event?.event_type === "calendar";

  const load = async () => {
    try {
      // Load from my events list
      const { data: events } = await api.get("/events");
      const ev = events.find((e) => e.id === id);
      if (!ev) throw new Error("Evento no encontrado");
      setEvent(ev);
      setTitle(ev.title);
      setDescription(ev.description || "");
      setEventDate(ev.event_date || "");
      setLocation(ev.location || "");
      if (ev.event_type === "calendar" && ev.calendar_settings) {
        setCalSettings({
          date_format: ev.calendar_settings.date_format || "dd/mm/yyyy",
          time_format: ev.calendar_settings.time_format || "12h",
          visibility: ev.calendar_settings.visibility || "public",
          field_email: ev.calendar_settings.field_email || "optional",
          field_phone: ev.calendar_settings.field_phone || "optional",
        });
      }
    } catch (err) {
      toast.error(formatApiError(err));
      nav("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
     
  }, [id]);

  const saveBasic = async () => {
    setSaving(true);
    try {
      const payload = { title, description, event_date: eventDate, location };
      if (isCalendar) payload.calendar_settings = calSettings;
      const { data } = await api.patch(`/events/${id}`, payload);
      setEvent(data);
      toast.success("Cambios guardados");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // ---- Potluck slot actions ----
  const addPotluckSlot = async () => {
    if (!newSlot.title.trim()) return toast.error("Ponle un título al espacio");
    try {
      const { data } = await api.post(`/events/${id}/slots`, {
        title: newSlot.title,
        description: "",
        quantity: parseInt(newSlot.quantity, 10) || 1,
        slot_type: newSlot.slot_type,
        when: newSlot.when || "",
      });
      setEvent(data);
      setNewSlot({ title: "", quantity: 1, when: "", slot_type: "item" });
      toast.success("Espacio agregado");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const patchPotluckSlot = async (slotId, patch) => {
    try {
      const { data } = await api.patch(`/events/${id}/slots/${slotId}`, patch);
      setEvent(data);
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      load();
      return false;
    }
  };

  const deletePotluckSlot = async (slotId) => {
    if (!window.confirm("¿Eliminar este espacio?")) return;
    try {
      await api.delete(`/events/${id}/slots/${slotId}`);
      toast.success("Espacio eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ---- Calendar slot actions ----
  const addCalendarSlots = async () => {
    if (addSlots.length === 0) return toast.error("Genera al menos un horario para agregar");
    try {
      const payload = {
        slots: addSlots.map((s) => ({ date: s.date, start_time: s.start_time, duration: s.duration })),
      };
      const { data } = await api.post(`/events/${id}/calendar-slots`, payload);
      setEvent(data);
      setAddDates([]);
      setAddSlots([]);
      toast.success("Horarios agregados");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteCalendarSlot = async (slotId) => {
    try {
      await api.delete(`/events/${id}/calendar-slots/${slotId}`);
      load();
      toast.success("Horario eliminado");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const slotsByDate = useMemo(() => {
    if (!isCalendar || !event) return new Map();
    const map = new Map();
    for (const s of event.calendar_slots || []) {
      if (!map.has(s.date)) map.set(s.date, []);
      map.get(s.date).push(s);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.start_time.localeCompare(b.start_time));
    return map;
  }, [event, isCalendar]);

  if (loading || !event)
    return (
      <div className="min-h-screen bg-cream-50">
        <Navbar />
        <p className="p-10 text-ink-500">Cargando…</p>
      </div>
    );

  const timeFormat = calSettings.time_format;

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-14">
        <Link to={`/eventos/${id}`} className="text-ink-500 hover:text-coral flex items-center gap-1 text-sm mb-6" data-testid="back-to-manage">
          <ChevronLeft size={16} strokeWidth={1.75} /> Volver a administración
        </Link>

        <span className="text-xs uppercase tracking-[0.24em] text-coral">Editar evento</span>
        <h1 className="mt-3 font-heading text-4xl md:text-5xl text-ink-900 font-light">
          {event.title}
        </h1>

        {/* Basic fields */}
        <div className="mt-10 space-y-6">
          <div className="bg-white border border-line rounded-2xl p-6 space-y-5">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-ink-500">Título</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="rounded-xl border-line h-12"
                data-testid="edit-title-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {!isCalendar && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-ink-500">Fecha</Label>
                  <Input
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    className="rounded-xl border-line h-12"
                    data-testid="edit-date-input"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-ink-500">Lugar</Label>
                <Input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="rounded-xl border-line h-12"
                  data-testid="edit-location-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-ink-500">Descripción</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="rounded-xl border-line min-h-[100px]"
                data-testid="edit-description-input"
              />
            </div>

            {isCalendar && (
              <div className="pt-4 border-t border-line space-y-5">
                <h3 className="font-heading text-xl text-ink-900">Ajustes del calendario</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-[11px] uppercase tracking-widest text-ink-500">Formato de fecha</Label>
                    <Select value={calSettings.date_format} onValueChange={(v) => setCalSettings({ ...calSettings, date_format: v })}>
                      <SelectTrigger className="rounded-xl border-line h-10 mt-1.5" data-testid="edit-date-format">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dd/mm/yyyy">DD/MM/AAAA (México)</SelectItem>
                        <SelectItem value="mm/dd/yyyy">MM/DD/AAAA</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-widest text-ink-500">Formato de hora</Label>
                    <Select value={calSettings.time_format} onValueChange={(v) => setCalSettings({ ...calSettings, time_format: v })}>
                      <SelectTrigger className="rounded-xl border-line h-10 mt-1.5" data-testid="edit-time-format">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="12h">12 horas (6:00 pm)</SelectItem>
                        <SelectItem value="24h">24 horas (18:00)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-ink-500 mb-2 block">
                    Visibilidad de los nombres
                  </Label>
                  <RadioGroup
                    value={calSettings.visibility}
                    onValueChange={(v) => setCalSettings({ ...calSettings, visibility: v })}
                    className="space-y-2"
                  >
                    <label className="flex items-start gap-3 border border-line rounded-xl p-3 cursor-pointer hover:bg-cream-50">
                      <RadioGroupItem value="public" data-testid="edit-visibility-public" />
                      <div>
                        <div className="text-sm font-medium text-ink-900">Todos ven quién se apuntó</div>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 border border-line rounded-xl p-3 cursor-pointer hover:bg-cream-50">
                      <RadioGroupItem value="private" data-testid="edit-visibility-private" />
                      <div>
                        <div className="text-sm font-medium text-ink-900">Solo el organizador</div>
                      </div>
                    </label>
                  </RadioGroup>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-ink-500 mb-2 block">
                    Campos al reservar (Nombre siempre es requerido)
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[11px] text-ink-500">Correo</Label>
                      <Select value={calSettings.field_email} onValueChange={(v) => setCalSettings({ ...calSettings, field_email: v })}>
                        <SelectTrigger className="rounded-xl border-line h-10 mt-1" data-testid="edit-field-email">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="required">Requerido</SelectItem>
                          <SelectItem value="optional">Opcional</SelectItem>
                          <SelectItem value="hidden">No pedir</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px] text-ink-500">Teléfono</Label>
                      <Select value={calSettings.field_phone} onValueChange={(v) => setCalSettings({ ...calSettings, field_phone: v })}>
                        <SelectTrigger className="rounded-xl border-line h-10 mt-1" data-testid="edit-field-phone">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="required">Requerido</SelectItem>
                          <SelectItem value="optional">Opcional</SelectItem>
                          <SelectItem value="hidden">No pedir</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button
                onClick={saveBasic}
                disabled={saving}
                className="rounded-full bg-coral hover:bg-coral-hover text-white px-6 h-11"
                data-testid="save-basic-btn"
              >
                <Save size={16} strokeWidth={1.75} className="mr-2" />
                {saving ? "Guardando…" : "Guardar cambios"}
              </Button>
            </div>
          </div>
        </div>

        {/* Slot management */}
        {!isCalendar && (
          <div className="mt-12">
            <h2 className="font-heading text-2xl text-ink-900 mb-4">Espacios</h2>
            <div className="space-y-3">
              {event.slots.map((s) => (
                <PotluckSlotRow
                  key={s.id}
                  slot={s}
                  onPatch={(patch) => patchPotluckSlot(s.id, patch)}
                  onDelete={() => deletePotluckSlot(s.id)}
                />
              ))}
              {event.slots.length === 0 && <p className="text-ink-500 text-sm">Sin espacios todavía.</p>}
            </div>

            <div className="mt-6 bg-white border border-line rounded-2xl p-5">
              <h3 className="font-heading text-lg text-ink-900 mb-4">Agregar nuevo espacio</h3>
              <div className="grid grid-cols-12 gap-3 items-end">
                <div className="col-span-12 md:col-span-5">
                  <Label className="text-[11px] uppercase tracking-widest text-ink-500">Qué / rol</Label>
                  <Input
                    value={newSlot.title}
                    onChange={(e) => setNewSlot({ ...newSlot, title: e.target.value })}
                    className="rounded-xl border-line h-11 mt-1"
                    placeholder="Ej: Postre"
                    data-testid="new-slot-title"
                  />
                </div>
                <div className="col-span-6 md:col-span-4">
                  <Label className="text-[11px] uppercase tracking-widest text-ink-500">Detalle</Label>
                  <Input
                    value={newSlot.when}
                    onChange={(e) => setNewSlot({ ...newSlot, when: e.target.value })}
                    className="rounded-xl border-line h-11 mt-1"
                    placeholder="Opcional"
                    data-testid="new-slot-when"
                  />
                </div>
                <div className="col-span-4 md:col-span-2">
                  <Label className="text-[11px] uppercase tracking-widest text-ink-500">Cupo</Label>
                  <Input
                    type="number"
                    min={1}
                    value={newSlot.quantity}
                    onChange={(e) => setNewSlot({ ...newSlot, quantity: e.target.value })}
                    className="rounded-xl border-line h-11 mt-1"
                    data-testid="new-slot-qty"
                  />
                </div>
                <div className="col-span-2 md:col-span-1 flex justify-end">
                  <Button
                    onClick={addPotluckSlot}
                    className="rounded-full bg-coral hover:bg-coral-hover text-white h-11 w-11 p-0"
                    aria-label="Agregar"
                    data-testid="new-slot-add"
                  >
                    <Plus size={18} strokeWidth={1.75} />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {isCalendar && (
          <div className="mt-12">
            <h2 className="font-heading text-2xl text-ink-900 mb-4">Horarios existentes</h2>
            <div className="space-y-4">
              {Array.from(slotsByDate.entries()).map(([date, list]) => (
                <div key={date} className="rounded-2xl overflow-hidden border border-line bg-white shadow-sm">
                  <div className="bg-cream-100 border-b border-line px-5 py-3">
                    <h3 className="font-heading text-lg text-ink-900">{formatLongDate(date)}</h3>
                  </div>
                  <ul className="divide-y divide-line">
                    {list.map((s) => (
                      <li key={s.id} className="px-5 py-3 flex items-center gap-4" data-testid={`edit-slot-${s.id}`}>
                        <div className="w-24 shrink-0 text-ink-500 font-medium text-sm flex items-center gap-1.5">
                          <Clock size={12} strokeWidth={1.75} className="opacity-60" />
                          {formatTime(s.start_time, timeFormat)}
                        </div>
                        <div className="flex-1 text-sm">
                          {s.booked ? (
                            <span className="text-ink-500">Reservado — cancela primero desde administración</span>
                          ) : (
                            <span className="text-ink-700">Disponible</span>
                          )}
                        </div>
                        <button
                          onClick={() => deleteCalendarSlot(s.id)}
                          disabled={!!s.booked}
                          className={`transition-colors ${s.booked ? "text-ink-300/50 cursor-not-allowed" : "text-ink-500 hover:text-coral"}`}
                          aria-label="Eliminar horario"
                          data-testid={`edit-slot-delete-${s.id}`}
                        >
                          <Trash2 size={16} strokeWidth={1.75} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {slotsByDate.size === 0 && <p className="text-ink-500 text-sm">Sin horarios todavía.</p>}
            </div>

            <div className="mt-10">
              <h2 className="font-heading text-2xl text-ink-900 mb-4">Agregar más días y horarios</h2>
              <CalendarBuilder
                selectedDates={addDates}
                onDatesChange={setAddDates}
                slots={addSlots}
                onSlotsChange={setAddSlots}
                timeFormat={timeFormat}
              />
              <div className="mt-6 flex justify-end">
                <Button
                  onClick={addCalendarSlots}
                  disabled={addSlots.length === 0}
                  className="rounded-full bg-coral hover:bg-coral-hover text-white px-6 h-11"
                  data-testid="add-cal-slots-btn"
                >
                  <Plus size={16} strokeWidth={1.75} className="mr-2" />
                  Agregar {addSlots.length > 0 ? `${addSlots.length} horario${addSlots.length !== 1 ? "s" : ""}` : "horarios"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PotluckSlotRow({ slot, onPatch, onDelete }) {
  const [title, setTitle] = useState(slot.title);
  const [when, setWhen] = useState(slot.when || "");
  const [quantity, setQuantity] = useState(slot.quantity);
  const [editing, setEditing] = useState(false);

  const save = async () => {
    const ok = await onPatch({
      title,
      when,
      quantity: parseInt(quantity, 10) || 1,
    });
    if (ok) {
      setEditing(false);
      toast.success("Espacio actualizado");
    }
  };

  const cancel = () => {
    setTitle(slot.title);
    setWhen(slot.when || "");
    setQuantity(slot.quantity);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className="bg-white border border-line rounded-2xl p-4 flex items-center gap-4">
        <div className="flex-1">
          <div className="font-medium text-ink-900">{slot.title}</div>
          {slot.when && <div className="text-xs text-ink-500 mt-0.5">{slot.when}</div>}
          <div className="text-xs text-sage mt-1">
            {slot.filled}/{slot.quantity} apuntados
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setEditing(true)}
          className="rounded-full border-line hover:bg-cream-100"
          data-testid={`edit-slot-btn-${slot.id}`}
        >
          Editar
        </Button>
        <button
          onClick={onDelete}
          disabled={slot.filled > 0}
          className={`${slot.filled > 0 ? "text-ink-300/50 cursor-not-allowed" : "text-ink-500 hover:text-coral"}`}
          aria-label="Eliminar"
          data-testid={`del-slot-btn-${slot.id}`}
        >
          <Trash2 size={16} strokeWidth={1.75} />
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-coral rounded-2xl p-4 grid grid-cols-12 gap-3 items-end">
      <div className="col-span-12 md:col-span-5">
        <Label className="text-[11px] uppercase tracking-widest text-ink-500">Qué / rol</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-xl border-line h-11 mt-1" />
      </div>
      <div className="col-span-6 md:col-span-4">
        <Label className="text-[11px] uppercase tracking-widest text-ink-500">Detalle</Label>
        <Input value={when} onChange={(e) => setWhen(e.target.value)} className="rounded-xl border-line h-11 mt-1" />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Label className="text-[11px] uppercase tracking-widest text-ink-500">Cupo</Label>
        <Input type="number" min={slot.filled || 1} value={quantity} onChange={(e) => setQuantity(e.target.value)} className="rounded-xl border-line h-11 mt-1" />
      </div>
      <div className="col-span-8 md:col-span-2 flex gap-2 justify-end">
        <Button size="sm" variant="outline" onClick={cancel} className="rounded-full border-line">
          Cancelar
        </Button>
        <Button size="sm" onClick={save} className="rounded-full bg-coral hover:bg-coral-hover text-white">
          Guardar
        </Button>
      </div>
    </div>
  );
}
